import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { max, min, sortBy, sum, values } from 'lodash';

import { SLICES_NAMES } from 'constants/app';
import { MarketGraphDataStatuses } from 'constants/marketGraph';
import { TFailureActionPayload, WebSocketClosedResponse } from 'types';
import { MarketGraphLiquidityTradedTableRow, MarketGraphStatus, MarketGraphWebSocketResponse } from 'types/marketGraph';

import { IGetMarketPayload, TMarketResponse } from '../market/type';

import { IMarketData, MarketGraphState, TCurrentSelection } from './type';

const initialState: MarketGraphState = {
  marketData: null,
  currentSelection: {
    marketId: null,
    selectionId: null,
    handicap: null
  },
  market: null,
  marketLoading: false,
  marketError: null,
  activeMarkets: {},
  chartData: {
    marketId: '',
    status: MarketGraphDataStatuses.pending,
    selectionId: '',
    points: []
  },
  chartUpdatedPoints: [],
  liquidityTradedTable: [],
  isMarketDataLoaded: false,
  bestBack: null
};

const slice = createSlice({
  name: SLICES_NAMES.MARKET_GRAPH,
  initialState,
  reducers: {
    setChartData: (state, { payload }: PayloadAction<MarketGraphWebSocketResponse | WebSocketClosedResponse>) => {
      if (payload === 'CLOSED') {
        state.chartData = initialState.chartData;
      } else if (payload.status === MarketGraphDataStatuses.updated) {
        state.chartData = {
          ...payload,
          points: state.chartData.points.concat(payload.points)
        };
        state.chartUpdatedPoints = payload.points;
      } else {
        state.chartData = payload;

        if (state.chartUpdatedPoints.length) {
          state.chartUpdatedPoints = [];
        }
      }
    },
    fetchGraphMarket: (state, __: PayloadAction<IGetMarketPayload>) => {
      state.marketLoading = true;
    },
    successFetchGraphMarket: (state, { payload }: PayloadAction<TMarketResponse>) => {
      state.marketLoading = false;
      state.market = payload as any;
      state.marketError = null;
    },
    failureFetchGraphMarket: (state, { payload }: PayloadAction<TFailureActionPayload>) => {
      state.marketError = payload;
      state.marketLoading = false;
    },
    checkSportsMarkets: (_, __: PayloadAction<{ marketIds: string[] }>) => {},
    successCheckSportsMarkets: (state, { payload }: PayloadAction<Record<string, boolean>>) => {
      state.activeMarkets = payload;
    },
    setCurrentSelection: (state, { payload }: PayloadAction<TCurrentSelection | null>) => {
      if (payload === null) {
        state.currentSelection = initialState.currentSelection;
      } else {
        state.currentSelection = payload;
      }
    },
    setMarketData: (state, { payload }: PayloadAction<IMarketData>) => {
      const atb = (payload.hasOwnProperty('rc') ? payload.rc?.[0].atb : state.marketData?.rc?.[0].atb) || [];
      const atl = (payload.hasOwnProperty('rc') ? payload.rc?.[0].atl : state.marketData?.rc?.[0].atl) || [];
      const trd = (payload.hasOwnProperty('rc') ? payload.rc?.[0].trd : state.marketData?.rc?.[0].trd) || [];

      const isSelectionChanged =
        payload.hasOwnProperty('rc') &&
        (state.marketData?.rc[0]?.id !== payload.rc?.[0].id || state.marketData?.rc[0]?.hc !== payload.rc?.[0].hc);

      const { trdOdds, trdAmounts } = trd.reduce(
        (acc: { trdOdds: number[]; trdAmounts: number[] }, [odd, amount]) => {
          acc.trdOdds.push(odd);
          acc.trdAmounts.push(amount);
          return acc;
        },
        { trdOdds: [], trdAmounts: [] }
      );

      const data = {
        ...payload,
        ...{ minPrice: min(trdOdds), maxPrice: max(trdOdds), totalMatched: sum(trdAmounts) }
      } as IMarketData;

      if (state.marketData?.id === data.id) {
        state.marketData = { ...state.marketData, ...data };
      } else {
        state.marketData = data;
      }

      const trdByOdds = trd.reduce((res: Record<number, MarketGraphLiquidityTradedTableRow>, [odd, amount]) => {
        return { ...res, [odd]: { odds: odd, back: '', lay: '', matched: amount, isBestBack: false } };
      }, {});

      const backValues: number[] = [];

      atb.forEach(([oddsVal, amountVal]) => {
        if (amountVal) {
          if (trdByOdds[oddsVal]) {
            trdByOdds[oddsVal].back = amountVal;
          } else {
            trdByOdds[oddsVal] = { odds: oddsVal, back: amountVal, lay: '', matched: '' };
          }

          backValues.push(oddsVal);
        }
      });

      atl.forEach(([oddsVal, amountVal]) => {
        if (amountVal) {
          if (trdByOdds[oddsVal]) {
            trdByOdds[oddsVal].lay = amountVal;
          } else {
            trdByOdds[oddsVal] = { odds: oddsVal, back: '', lay: amountVal, matched: '' };
          }
        }
      });

      const bestBack = max(backValues);

      if (bestBack && trdByOdds[bestBack] && isSelectionChanged) {
        trdByOdds[bestBack].isBestBack = true;
        state.bestBack = bestBack;
      }

      state.liquidityTradedTable = sortBy(values(trdByOdds), 'odds') as MarketGraphLiquidityTradedTableRow[];

      state.isMarketDataLoaded = true;
    },
    setIsMarketDataLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.isMarketDataLoaded = payload;
    },
    setMarketGraphChartStatus: (state, { payload }: PayloadAction<MarketGraphStatus>) => {
      state.chartData.status = payload;
    }
  }
});

export const {
  setChartData,
  fetchGraphMarket,
  successFetchGraphMarket,
  failureFetchGraphMarket,
  checkSportsMarkets,
  successCheckSportsMarkets,
  setCurrentSelection,
  setMarketData,
  setIsMarketDataLoaded,
  setMarketGraphChartStatus
} = slice.actions;

export default slice.reducer;
